/**
 * ConnectApprovalModal - the "you have Ns to approve on the OPBridge
 * dashboard" prompt (operator ask, 2026-08-12). Fires the moment the user
 * taps Connect on a candidate row in `ConnectPage.tsx`.
 *
 * Purely a presentation layer over ConnectPage's own pairing state
 * (`startPair`/`pollUntilResolved`/`cancelPair`/`tryAgain`) - no second
 * network path. The row's own inline countdown pill (DEC-060/061) still
 * exists underneath and keeps working if this modal is dismissed; this is
 * a louder, harder-to-miss announcement of the SAME wait, not a
 * replacement mechanism.
 *
 * Dismiss semantics, deliberately asymmetric:
 *   - backdrop click / Esc / the "Hide" affordance while WAITING just
 *     closes the modal - the pairing attempt keeps running in the
 *     background (the row still shows Waiting + its own countdown). The
 *     operator may want to go find the OPBridge dashboard without the
 *     modal blocking their view of anything else.
 *   - the "Cancel" button actually cancels the pairing attempt AND closes.
 *   - approved auto-closes shortly after a brief success flash.
 *   - denied/expired stay open until the operator acts (Try again / Close)
 *     - a failure is exactly the moment NOT to auto-vanish silently.
 *
 * Countdown ring reuses SineLfo's "armed countdown" recipe verbatim
 * (12-o'clock start via `rotate(-90deg)`, dashoffset = circumference x
 * elapsed fraction, round linecap + glow) so this reads as the same
 * design language as the rest of the app, not a one-off.
 */

import { createMemo, Show, onMount, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';

/** `unreachable` added 2026-08-28: the request threw (or resolving it did),
 *  which is NOT the same event as the operator pressing Deny and must not
 *  share its copy. See `ConnectPage.tsx`'s startPair for the evidence. */
export type ConnectApprovalOutcome = 'waiting' | 'approved' | 'denied' | 'expired' | 'unreachable';

interface ConnectApprovalModalProps {
  displayName: string;
  outcome: ConnectApprovalOutcome;
  remainingMs: number;
  totalMs: number;
  onCancel: () => void;
  onTryAgain: () => void;
  onClose: () => void;
}

const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

export function ConnectApprovalModal(props: ConnectApprovalModalProps) {
  onMount(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') props.onClose();
    }
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  const secondsLeft = createMemo(() => Math.max(0, Math.ceil(props.remainingMs / 1000)));
  const elapsedFraction = createMemo(() => clamp01(1 - props.remainingMs / Math.max(1, props.totalMs)));
  const ringDashoffset = createMemo(() => RING_C * elapsedFraction());

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div class="connect-approval-backdrop" onClick={() => props.onClose()}>
        <div
          class="connect-approval-dialog"
          data-outcome={props.outcome}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="OPBridge pairing approval"
        >
          <Show when={props.outcome === 'waiting'}>
            <div class="connect-approval-title">Approve on the OPBridge dashboard</div>
            <div class="connect-approval-body">
              Open the OPBridge dashboard on <strong>{props.displayName}</strong> and click
              Approve - you have <strong>{secondsLeft()}s</strong> before this request expires.
            </div>
            <div class="connect-approval-ring-wrap">
              <svg class="connect-approval-ring" viewBox="0 0 120 120" aria-hidden="true">
                <circle class="ca-track" cx="60" cy="60" r={RING_R} />
                <circle
                  class="ca-drain"
                  cx="60" cy="60" r={RING_R}
                  stroke-dasharray={String(RING_C)}
                  stroke-dashoffset={String(ringDashoffset())}
                />
              </svg>
              <div class="connect-approval-seconds">
                <span class="connect-approval-seconds-n">{secondsLeft()}</span>
                <span class="connect-approval-seconds-label">seconds</span>
              </div>
            </div>
            <div class="connect-approval-actions">
              <button type="button" class="connect-approval-cancel" onClick={() => props.onCancel()}>
                Cancel
              </button>
              <button type="button" class="connect-approval-hide" onClick={() => props.onClose()}>
                Hide
              </button>
            </div>
          </Show>

          <Show when={props.outcome === 'approved'}>
            <div class="connect-approval-outcome-icon" aria-hidden="true">✓</div>
            <div class="connect-approval-title">Connected</div>
            <div class="connect-approval-body">{props.displayName} approved this device.</div>
          </Show>

          <Show when={props.outcome === 'expired'}>
            <div class="connect-approval-outcome-icon" aria-hidden="true">⏱</div>
            <div class="connect-approval-title">Time's up</div>
            <div class="connect-approval-body">
              The request expired before it was approved. Tap Try again to send a new one.
            </div>
            <div class="connect-approval-actions">
              <button type="button" class="connect-approval-tryagain" onClick={() => props.onTryAgain()}>
                Try again
              </button>
              <button type="button" class="connect-approval-hide" onClick={() => props.onClose()}>
                Close
              </button>
            </div>
          </Show>

          {/* Distinct from 'denied' on purpose: OPBridge cannot deny without a
              human pressing Deny, so a network-side failure must never be
              reported as the operator's decision. The copy names the ONE thing
              the operator can act on, and points at the console for the real
              error rather than pretending we know which link broke. */}
          <Show when={props.outcome === 'unreachable'}>
            <div class="connect-approval-outcome-icon" aria-hidden="true">⚠</div>
            <div class="connect-approval-title">Couldn't reach {props.displayName}</div>
            <div class="connect-approval-body">
              The request never completed, so {props.displayName} may not have seen it.
              Nothing was denied. Tap Try again.
            </div>
            <div class="connect-approval-actions">
              <button type="button" class="connect-approval-tryagain" onClick={() => props.onTryAgain()}>
                Try again
              </button>
              <button type="button" class="connect-approval-hide" onClick={() => props.onClose()}>
                Close
              </button>
            </div>
          </Show>

          <Show when={props.outcome === 'denied'}>
            <div class="connect-approval-outcome-icon" aria-hidden="true">✕</div>
            <div class="connect-approval-title">Not approved</div>
            <div class="connect-approval-body">
              The request to <strong>{props.displayName}</strong> was denied or could not be sent.
            </div>
            <div class="connect-approval-actions">
              <button type="button" class="connect-approval-tryagain" onClick={() => props.onTryAgain()}>
                Try again
              </button>
              <button type="button" class="connect-approval-hide" onClick={() => props.onClose()}>
                Close
              </button>
            </div>
          </Show>
        </div>
      </div>
    </Portal>
  );
}
